import { useRef, useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Alert, Avatar, Box, Button, CircularProgress, Collapse, Divider, IconButton, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BoltIcon from '@mui/icons-material/Bolt';
import InsightsIcon from '@mui/icons-material/Insights';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useLoanChat from '../../hooks/useLoanChat';
import { MAX_MESSAGE_LENGTH } from '../../services/loanChatApi';

const PANEL_WIDTH = 440;

// Per-category icon + accent color for the quick-action tiles
const CATEGORY_STYLES = {
  deal_optimization: { Icon: InsightsIcon, color: '#5b6ef5' },
  pricing_optimization: { Icon: SavingsOutlinedIcon, color: '#2e9e6b' },
  borrower_coaching: { Icon: SchoolOutlinedIcon, color: '#d9822b' },
  scenario_modeling: { Icon: TuneIcon, color: '#8b5cf6' },
};

const DEFAULT_CATEGORY_STYLE = { Icon: BoltIcon, color: '#5b6ef5' };

const getCategoryStyle = (categoryKey) => CATEGORY_STYLES[categoryKey] || DEFAULT_CATEGORY_STYLE;

const markdownComponents = {
  table: ({ children }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ my: 1 }}>
      <Table size="small">{children}</Table>
    </TableContainer>
  ),
  thead: ({ children }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{children}</TableCell>,
  td: ({ children }) => <TableCell sx={{ fontSize: '0.75rem' }}>{children}</TableCell>,
  // Research citations: open in a new tab without exposing the opener
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
      {children}
    </Link>
  ),
};

const renderMarkdown = (content) => (
  <Box sx={{ '& p': { m: 0, mb: 1 }, '& p:last-child': { mb: 0 }, fontSize: '0.85rem', lineHeight: 1.6 }}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]} components={markdownComponents}>
      {content}
    </ReactMarkdown>
  </Box>
);

const AssistantAvatar = () => (
  <Avatar sx={{ width: 24, height: 24, bgcolor: 'action.selected', mr: 1 }}>
    <SmartToyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
  </Avatar>
);

/** A single quick-action card: icon, label, and an explanatory description. */
const QuickActionTile = ({ preset, accent, Icon, disabled, onSelect }) => (
  <Paper
    variant="outlined"
    onClick={disabled ? undefined : onSelect}
    sx={{
      p: 1.25,
      borderRadius: 2,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'all 0.15s ease',
      borderColor: 'divider',
      '&:hover': disabled ? {} : {
        borderColor: accent,
        bgcolor: 'action.hover',
        boxShadow: 1,
        transform: 'translateY(-1px)',
      },
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          flexShrink: 0,
          width: 30,
          height: 30,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${accent}1a`,
          color: accent,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
          {preset.label}
        </Typography>
        {preset.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
            {preset.description}
          </Typography>
        )}
      </Box>
      <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0, mt: 0.25 }} />
    </Stack>
  </Paper>
);

/**
 * Reusable AI Chat Panel component with SSE streaming and markdown rendering.
 *
 * Props:
 *   loanId    - (string) The loan GUID to chat about (required)
 *   isOpen    - (bool)   Whether the panel is expanded
 *   onToggle  - (func)   Callback to toggle panel open/closed
 */
const AIChatPanel = ({
  loanId,
  isOpen,
  onToggle,
  quickActionsEnabled = true,
  scenarioEnabled = true,
  feedbackEnabled = true,
}) => {
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const {
    messages,
    input,
    setInput,
    streamingContent,
    statusMessage,
    isStreaming,
    isLoadingHistory,
    isLoadingPresets,
    historyError,
    presetsError,
    error,
    presetCategories,
    feedbackByMessageId,
    canRegenerate,
    sendMessage,
    regenerate,
    stopStreaming,
    clearChat,
    sendPreset,
    submitFeedback,
  } = useLoanChat({ loanId, isOpen, quickActionsEnabled, quickActionsExpanded: quickActionsOpen, scenarioEnabled });

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const isAtBottomRef = useRef(true);

  // Track whether the user is scrolled to the bottom of the messages area
  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const threshold = 40;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  }, []);

  // Auto-scroll to bottom only when the user is already at the bottom
  useEffect(() => {
    if (isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingContent]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    isAtBottomRef.current = true;
    sendMessage();
  };

  const handleCopy = (content) => {
    navigator.clipboard?.writeText(content).catch((err) => console.error('Failed to copy message:', err));
  };

  const renderAssistantActions = (msg) => (
    <Stack direction="row" spacing={0} sx={{ mt: 0.25, ml: 4 }}>
      <Tooltip title="Copy">
        <IconButton size="small" onClick={() => handleCopy(msg.content)}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      {feedbackEnabled && msg.id != null && (
        <>
          <Tooltip title="Helpful response">
            <IconButton
              size="small"
              color={feedbackByMessageId[msg.id] === 1 ? 'success' : 'default'}
              onClick={() => submitFeedback(1, msg.id)}
            >
              <ThumbUpAltOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Not helpful response">
            <IconButton
              size="small"
              color={feedbackByMessageId[msg.id] === -1 ? 'error' : 'default'}
              onClick={() => submitFeedback(-1, msg.id)}
            >
              <ThumbDownAltOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Stack>
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        right: isOpen ? 0 : `-${PANEL_WIDTH}px`,
        top: 80,
        width: PANEL_WIDTH,
        height: 'calc(100vh - 100px)',
        bgcolor: 'background.paper',
        boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
        transition: 'right 0.3s ease',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 1,
        borderColor: 'divider',
      }}
    >
      {/* Toggle Button */}
      <IconButton
        onClick={onToggle}
        color="primary"
        sx={{
          position: 'absolute',
          left: -40,
          top: 20,
          width: 40,
          height: 40,
          borderRadius: '8px 0 0 8px',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          boxShadow: '-2px 2px 5px rgba(0,0,0,0.1)',
          '&:hover': { bgcolor: 'primary.dark' },
        }}
      >
        {isOpen ? <CloseIcon /> : <SmartToyIcon />}
      </IconButton>

      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AutoAwesomeIcon color="secondary" sx={{ fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700}>Loan AI Assistant</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">Ask anything about this loan</Typography>
          </Box>
          {messages.length > 0 && (
            <Tooltip title="Clear conversation">
              <span>
                <IconButton
                  size="small"
                  onClick={clearChat}
                  disabled={isStreaming}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* Messages Area */}
      <Box ref={messagesContainerRef} onScroll={handleScroll} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {!isLoadingHistory && quickActionsEnabled && (
          <Box sx={{ mb: 2 }}>
            {/* Collapsible header with caret */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={() => setQuickActionsOpen((prev) => !prev)}
              sx={{
                cursor: 'pointer',
                py: 0.5,
                userSelect: 'none',
                '&:hover .qa-label': { color: 'text.primary' },
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
              <Typography
                className="qa-label"
                variant="subtitle2"
                fontWeight={700}
                sx={{ flex: 1, color: 'text.secondary', transition: 'color 0.15s ease' }}
              >
                Quick Actions
              </Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  color: 'text.secondary',
                  transition: 'transform 0.2s ease',
                  transform: quickActionsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Stack>

            <Collapse in={quickActionsOpen} timeout="auto" unmountOnExit>
              <Box sx={{ pt: 1 }}>
                {isLoadingPresets ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Loading presets...</Typography>
                  </Stack>
                ) : presetsError ? (
                  <Alert severity="warning" sx={{ py: 0 }}>{presetsError}</Alert>
                ) : (
                  <Stack spacing={1.5}>
                    {presetCategories.map((category) => {
                      const { Icon, color } = getCategoryStyle(category.categoryKey);
                      return (
                        <Box key={category.categoryKey}>
                          <Typography
                            variant="overline"
                            sx={{ display: 'block', mb: 0.5, color: 'text.disabled', fontWeight: 700, letterSpacing: 0.4 }}
                          >
                            {category.categoryLabel}
                          </Typography>
                          <Stack spacing={0.75}>
                            {category.presets?.map((preset) => (
                              <QuickActionTile
                                key={preset.presetKey}
                                preset={preset}
                                accent={color}
                                Icon={Icon}
                                disabled={isStreaming}
                                onSelect={() => {
                                  setQuickActionsOpen(false);
                                  sendPreset(preset.promptTemplate, preset.intent, preset.presetKey);
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Collapse>

            <Divider sx={{ mt: 1.5 }} />
          </Box>
        )}

        {isLoadingHistory && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary" component="span">Loading conversation...</Typography>
          </Box>
        )}

        {!isLoadingHistory && historyError && (
          <Alert severity="warning" sx={{ mb: 2 }}>{historyError}</Alert>
        )}

        {!isLoadingHistory && messages.length === 0 && !isStreaming && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <ChatIcon sx={{ fontSize: 48, color: 'text.disabled', opacity: 0.3 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Ask a question about this loan file</Typography>
            <Typography variant="caption" color="text.disabled">
              Try: &quot;What is the DTI ratio?&quot; or &quot;Is this borrower eligible for FHA?&quot;
            </Typography>
          </Box>
        )}

        {messages.map((msg, i) => (
          <Box key={msg.id ?? `msg-${i}`} sx={{ mb: 2, display: 'flex', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
            <Box sx={{ maxWidth: '90%' }}>
              {msg.role === 'user' ? (
                <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                </Paper>
              ) : (
                <Box>
                  <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                    <AssistantAvatar />
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>AI Assistant</Typography>
                  </Stack>
                  {renderMarkdown(msg.content)}
                  {!isStreaming && renderAssistantActions(msg)}
                </Box>
              )}
            </Box>
          </Box>
        ))}

        {/* Streaming content */}
        {isStreaming && streamingContent && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ maxWidth: '90%' }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                <AssistantAvatar />
                <Typography variant="caption" color="text.secondary" fontWeight={700}>AI Assistant</Typography>
              </Stack>
              {renderMarkdown(streamingContent)}
            </Box>
          </Box>
        )}

        {/* Streaming indicator */}
        {isStreaming && !streamingContent && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <AssistantAvatar />
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">{statusMessage || 'Thinking...'}</Typography>
          </Stack>
        )}

        {/* Error display */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Regenerate */}
        {canRegenerate && (
          <Stack direction="row" justifyContent="center" sx={{ mb: 1 }}>
            <Button
              size="small"
              color="inherit"
              startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
              onClick={regenerate}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Regenerate response
            </Button>
          </Stack>
        )}

        <Box ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <Stack direction="row" spacing={1}>
          <TextField
            inputRef={inputRef}
            placeholder="Ask about this loan..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isStreaming}
            multiline
            maxRows={4}
            size="small"
            fullWidth
            slotProps={{ htmlInput: { maxLength: MAX_MESSAGE_LENGTH } }}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
          />
          {isStreaming ? (
            <IconButton color="error" onClick={stopStreaming} title="Stop generating">
              <StopIcon />
            </IconButton>
          ) : (
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={handleSend}
              disabled={!input.trim() || !loanId}
              sx={{ minWidth: 96 }}
            >
              Send
            </Button>
          )}
        </Stack>
        {input.length >= MAX_MESSAGE_LENGTH * 0.9 && (
          <Typography variant="caption" color={input.length >= MAX_MESSAGE_LENGTH ? 'error' : 'text.disabled'} sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
            {input.length}/{MAX_MESSAGE_LENGTH}
          </Typography>
        )}
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled">
            AI can make mistakes. Verify important information.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default AIChatPanel;
